import React, { useEffect, useState } from "react";
import BottomSheet from "./BottomSheet";
import AppStoreBadge from "./AppStoreBadge";
import { isNativeApp, useAppStoreStatus } from "../lib/appStore";
import "./AppStoreDownloadPrompt.css";
import { t as tr } from "../i18n";

// One-time "Download the app" sheet for DJs using the WEB app on an iPhone —
// the day the App Store listing goes live, this replaces the old
// "save to home screen" idea (founder decision). Shown once per device,
// never in the native app, never on desktop (the Mac companion banner owns
// that surface), and only when Apple actually lists the app.

export const APP_STORE_PROMPT_SEEN_KEY = "requestapp.appStorePromptSeen";

export const isIOSWeb = (): boolean => {
  try {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !isNativeApp();
  } catch {
    return false;
  }
};

export function shouldShowAppStorePrompt({ live, iosWeb, seen }: { live: boolean; iosWeb: boolean; seen: boolean }): boolean {
  return live && iosWeb && !seen;
}

const hasSeen = (): boolean => {
  try {
    return localStorage.getItem(APP_STORE_PROMPT_SEEN_KEY) === "1";
  } catch {
    return false;
  }
};
const markSeen = () => {
  try {
    localStorage.setItem(APP_STORE_PROMPT_SEEN_KEY, "1");
  } catch {}
};

const AppStoreDownloadPrompt: React.FC = () => {
  const status = useAppStoreStatus();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!shouldShowAppStorePrompt({ live: status.live, iosWeb: isIOSWeb(), seen: hasSeen() })) return;
    // Let the page land first; a sheet that beats the content feels like a popup ad.
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [status.live]);

  const close = () => {
    markSeen();
    setOpen(false);
  };

  if (!open || !status.url) return null;

  return (
    <BottomSheet open={open} onClose={close} plain title={tr("appStoreDownloadPrompt.downloadTheApp")}>
      <div className="asp">
        <img className="asp-wordmark" src="/request-wordmark.png" alt={tr("appStoreDownloadPrompt.requestApp")} />
        <p className="asp-line">{tr("appStoreDownloadPrompt.requestAppIsOn")}</p>
        <AppStoreBadge status={status} label={tr("appStoreDownloadPrompt.downloadTheApp")} onClick={close} className="asp-badge" />
        <button type="button" className="asp-later" onClick={close}>
          {tr("appStoreDownloadPrompt.notNow")}
        </button>
      </div>
    </BottomSheet>
  );
};

export default AppStoreDownloadPrompt;
